import { useEffect, useRef, useState } from 'react';

// Reproduces finkit-theme-switcher.js: 3 themes (light, dark, dark-blue).
export type ThemeMode = 'light' | 'dark' | 'dark-blue';

const MODES: { mode: ThemeMode; label: string; icon: string }[] = [
  { mode: 'light', label: 'Light', icon: 'ri-sun-line' },
  { mode: 'dark', label: 'Dark', icon: 'ri-moon-line' },
  { mode: 'dark-blue', label: 'Dark Blue', icon: 'ri-moon-clear-line' },
];

function isDark(mode: ThemeMode) {
  return mode === 'dark' || mode === 'dark-blue';
}

export function applyThemeMode(mode: ThemeMode) {
  const html = document.documentElement;
  const dark = isDark(mode);
  html.setAttribute('data-theme-mode', dark ? 'dark' : 'light');
  html.setAttribute('data-bs-theme', dark ? 'dark' : 'light');
  html.setAttribute('data-header-styles', dark ? 'dark' : 'light');
  html.setAttribute('data-menu-styles', dark ? 'dark' : 'light');
  if (mode === 'dark-blue') html.setAttribute('data-theme-style', 'blue');
  else html.removeAttribute('data-theme-style');

  try {
    localStorage.setItem('finkitThemeMode', mode);
    if (dark) {
      localStorage.setItem('noadarktheme', 'true');
      localStorage.setItem('noaMenu', 'dark');
      localStorage.setItem('noaHeader', 'dark');
    } else {
      localStorage.removeItem('noadarktheme');
      localStorage.removeItem('noaMenu');
      localStorage.removeItem('noaHeader');
    }
  } catch {
    /* ignore */
  }
}

export function resolveInitialMode(): ThemeMode {
  try {
    const stored = localStorage.getItem('finkitThemeMode');
    if (stored === 'light' || stored === 'dark' || stored === 'dark-blue') return stored;
    if (localStorage.getItem('noadarktheme')) return 'dark-blue';
  } catch {
    /* ignore */
  }
  return 'light';
}

export function ThemeSwitcher() {
  const [mode, setMode] = useState<ThemeMode>(resolveInitialMode);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    applyThemeMode(mode);
  }, [mode]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const current = MODES.find((m) => m.mode === mode) ?? MODES[0];

  return (
    <div className="header-element dropdown theme-switcher" ref={ref} style={{ position: 'relative' }}>
      <a
        href="#"
        className="header-link"
        onClick={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
        }}
        aria-label="Theme"
      >
        <i className={`${current.icon} header-link-icon fs-18`} />
      </a>
      <div
        className={`dropdown-menu dropdown-menu-end theme-switcher-menu ${open ? 'show' : ''}`}
        style={{ position: 'absolute', right: 0, left: 'auto', top: '100%', bottom: 'auto', transform: 'none', marginTop: '0.25rem' }}
      >
        {MODES.map((m) => (
          <button
            key={m.mode}
            type="button"
            className={`dropdown-item theme-option d-flex align-items-center gap-2${m.mode === mode ? ' active' : ''}`}
            onClick={() => {
              setMode(m.mode);
              setOpen(false);
            }}
          >
            <i className={`${m.icon} theme-option-icon`} />
            <span>{m.label}</span>
            {m.mode === mode && <i className="ri-check-line ms-auto theme-option-check" />}
          </button>
        ))}
      </div>
    </div>
  );
}
